import {
  DepotEvent,
  EventStatus,
  EventOrigin,
  EventAudience,
  DepotRef,
  EventType,
  PresetText,
  SalesChannel,
  Mailing,
  MailingDeleteResult,
  SaveMailingRequest,
  EventText,
  EventTextDeleteResult,
  SaveEventTextRequest,
  CancellationTerm,
  CancellationTermDeleteResult,
  SaveCancellationTermRequest,
  PaymentNoteTemplate,
  PaymentNoteTemplateDeleteResult,
  SavePaymentNoteTemplateRequest,
  RegistrationNoteTemplate,
  RegistrationNoteTemplateDeleteResult,
  SaveRegistrationNoteTemplateRequest,
  FinancialSupport,
  FinancialSupportDeleteResult,
  SaveFinancialSupportRequest,
  PaginatedResponse,
  EventListParams,
  CreateEventRequest,
  UpdateEventRequest,
  EventSeriesOccurrence,
  CreateEventSeriesResult,
  NotifyAdvertisingDepotsResult,
} from '@/types';

// ---- Fixtures ----------------------------------------------------------------

// Depots mirror the mock users in MockAuthProvider (D001..D005).
export const DEPOTS: DepotRef[] = [
  { key: 'D001', uuid: 'depot-uuid-001' },
  { key: 'D002', uuid: 'depot-uuid-002' },
  { key: 'D003', uuid: 'depot-uuid-003' },
  { key: 'D004', uuid: 'depot-uuid-004' },
  { key: 'D005', uuid: 'depot-uuid-005' },
];

// v1: read-only event types (seed values are placeholders — TBD from business team).
export const EVENT_TYPES: EventType[] = [
  { id: 'vt-tasting', name: 'Weinverkostung' },
  { id: 'vt-seminar', name: 'Seminar' },
  // hasExtraPeriod: true seeded on exactly one type, so the extra-period
  // mailing match is visible in mock mode without a backend connection.
  { id: 'vt-actionday', name: 'Aktionstag', hasExtraPeriod: true },
  { id: 'vt-pairing', name: 'Food-Pairing' },
  { id: 'vt-tour', name: 'Depot-Führung' },
];

// v1: read-only preset event texts (placeholders — TBD from business team).
export const PRESET_TEXTS: PresetText[] = [
  {
    id: 'preset-tasting',
    name: 'Standard: Weinverkostung',
    html: '<p>Erleben Sie einen genussvollen Abend mit ausgewählten Weinen und fachkundiger Begleitung.</p>',
  },
  {
    id: 'preset-seminar',
    name: 'Standard: Seminar',
    html: '<p>In diesem Seminar vermitteln wir Ihnen Grundlagen und Wissenswertes rund um das Thema Wein.</p>',
  },
];

// Sales channels for quota creation. Keys mirror the ticket-api SalesChannel enum
// (commerce-engine, depot-pos) — the quota's sales_channels[] is created from this.
export const SALES_CHANNELS: SalesChannel[] = [
  { id: 'commerce-engine', name: 'Commerce Engine (Webshop)' },
  { id: 'depot-pos', name: 'Depot-Kasse (POS)' },
];

// Mailings shaped like the real planning table, including one whose cutoff has
// already passed and one carrying a period 2, so the panel's states (eligible,
// expired, with/without a second period) are all visible in mock mode without
// an import.
export const MAILINGS: Mailing[] = [
  {
    key: 'FEB_A_2026',
    title: 'FEB A 2026',
    period1From: '2026-02-16',
    period1To: '2026-03-01',
    period2From: '2026-03-30',
    period2To: '2026-04-12',
    onlineDate: '2026-01-22',
    cutoffDate: '2026-01-18',
    deliveryDate: '2026-02-06',
  },
  {
    key: 'FEB_B_2026',
    title: 'FEB B 2026',
    period1From: '2026-03-02',
    period1To: '2026-03-15',
    period2From: '2026-04-13',
    period2To: '2026-04-26',
    onlineDate: '2026-02-05',
    cutoffDate: '2026-02-01',
    deliveryDate: '2026-02-20',
  },
  {
    key: 'MRZ_A_2026',
    title: 'MRZ A 2026',
    period1From: '2026-03-16',
    period1To: '2026-03-29',
    onlineDate: '2026-02-19',
    // Bewusst in der Vergangenheit, damit der „abgelaufen"-Zustand im Mock-Modus
    // sichtbar ist.
    cutoffDate: '2026-02-15',
    deliveryDate: '2026-03-06',
  },
  {
    key: 'MRZ_B_2026',
    title: 'MRZ B 2026',
    period1From: '2026-03-30',
    period1To: '2026-04-12',
    onlineDate: '2026-03-05',
    cutoffDate: '2026-03-01',
    deliveryDate: '2026-03-20',
  },
];

// Veränderlicher Stand für den Mock-Modus. MAILINGS bleibt der Startwert, damit ein
// Reload wieder dort anfängt; ohne diese Kopie wäre die „Datenbank" die Konstante
// und jede Änderung würde den Startwert selbst verbiegen.
let mockMailings: Mailing[] = [...MAILINGS];

const TITLES = [
  'Sommerliche Weinverkostung',
  'Riesling & Käse Abend',
  'Rotwein-Seminar für Einsteiger',
  'Tag der offenen Tür',
  'Winzer zu Gast',
  'Herbstliche Federweißer-Probe',
  'Sekt & Champagner Tasting',
  'Bio-Weine entdecken',
];

const LOCATION_TYPES = ['depot', 'external', 'online'] as const;

/** Lebenszyklus-Reihenfolge für die Status-Sortierung — gespiegelt vom Backend (sortEvents(), zuletzt 24.09.2026). */
const STATUS_ORDER: Record<EventStatus, number> = {
  [EventStatus.Initial]: 0,
  [EventStatus.Rejected]: 1,
  [EventStatus.InReview]: 2,
  [EventStatus.Approved]: 3,
  [EventStatus.Online]: 4,
  [EventStatus.Completed]: 5,
  [EventStatus.Cancelled]: 6,
};

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Deterministic-ish future datetime within N days ahead, at the given hour.
function futureDate(daysAhead: number, hour = 18): string {
  const date = new Date();
  date.setDate(date.getDate() + Math.floor(Math.random() * daysAhead));
  date.setHours(hour, 0, 0, 0);
  return date.toISOString();
}

function pastDate(daysBack: number): string {
  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * daysBack));
  return date.toISOString();
}

// endAt = startAt + given hours
function plusHours(iso: string, hours: number): string {
  const d = new Date(iso);
  d.setHours(d.getHours() + hours);
  return d.toISOString();
}

let idCounter = 0;
function nextId(): string {
  idCounter += 1;
  return `evt-${String(idCounter).padStart(4, '0')}`;
}

function generateEvents(count: number): DepotEvent[] {
  return Array.from({ length: count }, () => {
    const statusRoll = Math.random();
    const status =
      statusRoll < 0.5
        ? EventStatus.Initial
        : statusRoll < 0.8
        ? EventStatus.InReview
        : EventStatus.Approved;
    const origin = Math.random() < 0.75 ? EventOrigin.Partner : EventOrigin.Central;
    const depot = randomItem(DEPOTS);
    const locationType = randomItem([...LOCATION_TYPES]);
    const createdAt = pastDate(120);
    const businessHours = Math.random() < 0.4;
    const startAt = futureDate(90);
    const id = nextId();

    const base: DepotEvent = {
      identifier: id,
      // Mock-Modus hat keine echte CT-/ticket-api-Anbindung — identifier
      // dient hier für beides (21.09.2026-Follow-up, event-id im VA-Modal).
      ctId: id,
      createdAt,
      updatedAt: createdAt,
      createdBy: origin === EventOrigin.Partner ? `partner.${depot.key.toLowerCase()}@jacques.de` : 'zentrale@jacques.de',
      modifiedBy: origin === EventOrigin.Partner ? `partner.${depot.key.toLowerCase()}@jacques.de` : 'zentrale@jacques.de',
      status,
      origin,
      depots: [depot],
      promotingDepots: Math.random() < 0.3 ? [depot, randomItem(DEPOTS)] : [depot],
      vaType: randomItem(EVENT_TYPES).id,
      title: randomItem(TITLES),
      teaser: 'Ein besonderes Erlebnis in Ihrem Depot.',
      description: '<p>Weitere Informationen folgen.</p>',
      audience: Math.random() < 0.7 ? EventAudience.Public : EventAudience.Closed,
      startAt,
      endAt: plusHours(startAt, 3),
      businessHours,
      costEnabled: Math.random() < 0.5,
      priceUnit: 'person',
      registrationRequired: Math.random() < 0.6,
      locationType,
    };

    // Kontingent: qty = max Plätze, available = noch verfügbar (Backend-Seam).
    const qty = randomItem([20, 30, 40, 50, 80, 100]);
    base.qty = qty;
    base.available = qty - Math.floor(Math.random() * (qty + 1));

    if (base.costEnabled) {
      base.priceAmount = randomItem([10, 15, 20, 25]);
    }
    if (base.registrationRequired) {
      base.registrationChannel = randomItem(['online', 'email', 'phone', 'in-depot']);
      base.registrationDeadline = futureDate(60).slice(0, 10);
    }
    if (locationType === 'external') {
      base.externalName = 'Stadthalle';
      base.externalStreet = 'Marktplatz';
      base.externalHouseNo = '1';
      base.externalZip = '50667';
      base.externalCity = 'Köln';
      base.externalCountry = 'Deutschland';
      base.externalLink = 'https://example.com/venue';
    }
    if (status === EventStatus.Initial && Math.random() < 0.3) {
      // some previously rejected events carry a reason
      base.rejectionReason = 'Bitte Uhrzeit und Anmeldeweg ergänzen.';
    }
    return base;
  });
}

// ---- Mutable store -----------------------------------------------------------

let mockEvents: DepotEvent[] = generateEvents(60);

// ---- Query -------------------------------------------------------------------

export function getMockEvents(
  params: EventListParams
): PaginatedResponse<DepotEvent> {
  let items = [...mockEvents];

  if (params.status && params.status.length > 0) {
    items = items.filter((e) => params.status!.includes(e.status));
  }
  if (params.origin && params.origin.length > 0) {
    items = items.filter((e) => params.origin!.includes(e.origin));
  }
  if (params.audience && params.audience.length > 0) {
    items = items.filter((e) => params.audience!.includes(e.audience));
  }
  if (params.depotKeys && params.depotKeys.length > 0) {
    items = items.filter((e) =>
      e.depots.some((d) => params.depotKeys!.includes(d.key))
    );
  }
  if (params.search) {
    const q = params.search.toLowerCase();
    // Volltext statt Präfix (22.09.2026-Follow-up) — gespiegelt vom Backend.
    items = items.filter((e) => e.title.toLowerCase().includes(q));
  }
  const eventDate = (e: DepotEvent) => new Date(e.startAt ?? e.createdAt);
  if (typeof params.year === 'number') {
    items = items.filter((e) => eventDate(e).getFullYear() === params.year);
  }
  if (typeof params.month === 'number') {
    items = items.filter((e) => eventDate(e).getMonth() + 1 === params.month);
  }
  if (params.dateFrom) {
    const from = new Date(params.dateFrom).getTime();
    items = items.filter((e) => eventDate(e).getTime() >= from);
  }
  if (params.dateTo) {
    const to = new Date(params.dateTo).getTime();
    items = items.filter((e) => eventDate(e).getTime() <= to);
  }
  if (params.endedBefore) {
    // Anders als dateFrom/dateTo oben (Bereichsüberlappung auf startAt/
    // createdAt): reiner "schon vorbei"-Schnitt auf endAt, für den Partner-
    // Schnellzugriff "Veranstaltungen abschließen" (22.09.2026-Follow-up).
    const cutoff = new Date(params.endedBefore).getTime();
    items = items.filter((e) => new Date(e.endAt ?? e.startAt ?? e.createdAt).getTime() < cutoff);
  }
  if (params.refundStatus && params.refundStatus.length > 0) {
    // Mock-Modus hat kein echtes Refund-Konzept (die Abrechnung läuft dort
    // schon insgesamt nicht mit) — jedes Event gilt als "noch keine
    // Abrechnung", der Sentinel 'none' passt also auf alle, jeder echte
    // ticket-api-Status auf keins (22.09.2026-Follow-up).
    items = params.refundStatus.includes('none') ? items : [];
  }
  if (params.lowContingent) {
    // Mock-Modus hat keine echte Kontingent-Simulation (kein qty/available auf
    // den Fake-Events, siehe createMockEventSeries's Destructuring oben) —
    // "Kontingent prüfen" kann hier folgerichtig nie etwas finden (25.09.2026).
    items = [];
  }

  // Sort — ohne explizite Auswahl greift derselbe Mehrfachschlüssel-Standard
  // wie im echten Backend (sortEvents()): Datum ab, Depot auf, Titel auf.
  // startAt/status sind seit 22.09.2026 selbst Mehrfachschlüssel (der jeweils
  // andere der beiden bricht Gleichstände, immer aufsteigend, dann Depot
  // aufsteigend) — status zusätzlich in Lebenszyklus-, nicht alphabetischer
  // Reihenfolge. Alle übrigen Felder bleiben Einzelfeld-Sortierung wie bisher.
  if (params.sortBy === 'startAt' || params.sortBy === 'status') {
    const sortBy = params.sortBy;
    const sortOrder = params.sortOrder ?? 'desc';
    const dir = sortOrder === 'asc' ? 1 : -1;
    items.sort((a, b) => {
      if (sortBy === 'startAt') {
        if (a.startAt !== b.startAt) return (a.startAt < b.startAt ? -1 : 1) * dir;
        const statusCmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        if (statusCmp) return statusCmp;
      } else {
        const statusCmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        if (statusCmp) return statusCmp * dir;
        if (a.startAt !== b.startAt) return a.startAt < b.startAt ? -1 : 1;
      }
      return (a.depots[0]?.key ?? '').localeCompare(b.depots[0]?.key ?? '');
    });
  } else if (params.sortBy) {
    const sortBy = params.sortBy;
    const sortOrder = params.sortOrder ?? 'desc';
    items.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortBy];
      const bv = (b as unknown as Record<string, unknown>)[sortBy];
      const as = av == null ? '' : String(av);
      const bs = bv == null ? '' : String(bv);
      const cmp = as.localeCompare(bs);
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  } else {
    items.sort((a, b) => {
      if (a.startAt !== b.startAt) return a.startAt < b.startAt ? 1 : -1;
      const depotCmp = (a.depots[0]?.key ?? '').localeCompare(b.depots[0]?.key ?? '');
      if (depotCmp) return depotCmp;
      return a.title.localeCompare(b.title);
    });
  }

  const total = items.length;
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 25;
  const start = (page - 1) * pageSize;
  const paged = items.slice(start, start + pageSize);

  return {
    items: paged,
    total,
    page,
    pageSize,
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
  };
}

export function getMockEvent(identifier: string): DepotEvent {
  const found = mockEvents.find((e) => e.identifier === identifier);
  if (!found) throw new Error(`Event not found: ${identifier}`);
  return { ...found };
}

export function getMockEventTypes(): EventType[] {
  return [...EVENT_TYPES];
}

export function getMockPresetTexts(): PresetText[] {
  return [...PRESET_TEXTS];
}

export function getMockSalesChannels(): SalesChannel[] {
  return [...SALES_CHANNELS];
}

export function getMockMailings(): Mailing[] {
  return [...mockMailings].sort((a, b) => a.period1From.localeCompare(b.period1From));
}

/**
 * Legt ein Mailing an oder ändert es (Mock-Gegenstück zu PUT /api/mailings).
 *
 * Die Schlüsselableitung wird hier NICHT nachgebaut: `toMailingKey` ist eine
 * Backend-Regel und gehört nicht ins Frontend. Der Mock prüft deshalb nur
 * auf gleiche Bezeichnung und setzt `key` gleich dem Titel — anders als das
 * echte Backend, aber für lokale UI-Zwecke ausreichend.
 */
export function saveMockMailing(req: SaveMailingRequest): Mailing {
  const title = req.title.trim();

  const collision = mockMailings.find((m) => m.title === title && m.title !== req.previousTitle);
  if (collision) {
    throw new Error(`Diese Bezeichnung ist bereits vergeben: ${title}`);
  }

  const saved: Mailing = {
    key: title,
    title,
    period1From: req.period1From,
    period1To: req.period1To,
    period2From: req.period2From,
    period2To: req.period2To,
    onlineDate: req.onlineDate,
    cutoffDate: req.cutoffDate,
    deliveryDate: req.deliveryDate,
  };

  // Den ersetzten Satz entfernen, auch bei Umbenennung — sonst bleibt der alte stehen.
  mockMailings = [
    ...mockMailings.filter((m) => m.title !== title && m.title !== req.previousTitle),
    saved,
  ];

  return saved;
}

/** Löscht Mailings (Mock-Gegenstück zu POST /api/mailings/bulk-delete). */
export function deleteMockMailings(titles: string[]): MailingDeleteResult {
  const present = new Set(mockMailings.map((m) => m.title));

  const removed = titles.filter((title) => present.has(title));
  const notFound = titles.filter((title) => !present.has(title));

  const doomed = new Set(removed);
  mockMailings = mockMailings.filter((m) => !doomed.has(m.title));

  // Im Mock kann pro Eintrag nichts fehlschlagen.
  return { removed, notFound, failed: [] };
}

// Textvorlagen. Kategorien absichtlich verschieden, damit die Kaskade Kategorie →
// Vorlage im Mock-Betrieb überhaupt etwas zu tun hat.
let mockEventTexts: EventText[] = [
  {
    id: '11111111-1111-4111-8111-111111111111',
    category: 'Verkostung',
    title: 'Verkostung (Standard)',
    text: '<p>Begleiten Sie uns durch eine Auswahl unserer Weine.</p>',
    createdAt: '2026-07-01T08:00:00.000Z',
    modifiedAt: '2026-07-01T08:00:00.000Z',
  },
  {
    id: '22222222-2222-4222-8222-222222222222',
    category: 'Seminar',
    title: 'Weinseminar (Standard)',
    text: '<p>Grundlagen der Weinsensorik. <strong>Anmeldung erforderlich.</strong></p>',
    createdAt: '2026-07-01T08:00:00.000Z',
    modifiedAt: '2026-07-01T08:00:00.000Z',
  },
  {
    id: '33333333-3333-4333-8333-333333333333',
    category: 'Seminar',
    title: 'Weinseminar für Fortgeschrittene',
    text: '<p>Vertiefung: Terroir, Ausbau, Reifepotenzial.</p>',
    createdAt: '2026-07-01T08:00:00.000Z',
    modifiedAt: '2026-07-01T08:00:00.000Z',
  },
  {
    id: '44444444-4444-4444-8444-444444444444',
    category: 'Kulinarik',
    title: 'Weindinner (Standard)',
    text: '<p>Ein mehrgängiges Menü, zu jedem Gang ein passender Wein.</p>',
    createdAt: '2026-07-01T08:00:00.000Z',
    modifiedAt: '2026-07-01T08:00:00.000Z',
  },
];

export function getMockEventTexts(): EventText[] {
  return [...mockEventTexts].sort(
    (a, b) =>
      a.category.localeCompare(b.category, 'de') || a.title.localeCompare(b.title, 'de')
  );
}

/**
 * Legt eine Vorlage an oder ändert sie (Mock-Gegenstück zu PUT /api/event-texts).
 *
 * Die HTML-Reinigung wird hier NICHT nachgebaut: sie gehört ins Backend, und ein
 * zweiter Sanitizer im Frontend würde beim nächsten Umbau auseinanderlaufen.
 */
export function saveMockEventText(req: SaveEventTextRequest): EventText {
  const jetzt = new Date().toISOString();

  if (!req.id) {
    const angelegt: EventText = {
      id: `mock-${mockEventTexts.length + 1}-${jetzt}`,
      category: req.category.trim(),
      title: req.title.trim(),
      text: req.text,
      createdAt: jetzt,
      modifiedAt: jetzt,
      modifiedBy: 'mock@localhost',
    };
    mockEventTexts = [...mockEventTexts, angelegt];
    return angelegt;
  }

  const vorhanden = mockEventTexts.find((t) => t.id === req.id);
  if (!vorhanden) {
    throw new Error('Diese Vorlage existiert nicht mehr.');
  }

  const geaendert: EventText = {
    ...vorhanden,
    category: req.category.trim(),
    title: req.title.trim(),
    text: req.text,
    // createdAt bleibt: Anlagezeitpunkt, nicht der dieser Fassung.
    modifiedAt: jetzt,
    modifiedBy: 'mock@localhost',
  };

  mockEventTexts = mockEventTexts.map((t) => (t.id === geaendert.id ? geaendert : t));
  return geaendert;
}

/** Löscht Vorlagen (Mock-Gegenstück zu POST /api/event-texts/bulk-delete). */
export function deleteMockEventTexts(ids: string[]): EventTextDeleteResult {
  const present = new Set(mockEventTexts.map((t) => t.id));

  const removed = ids.filter((id) => present.has(id));
  const notFound = ids.filter((id) => !present.has(id));

  const doomed = new Set(removed);
  mockEventTexts = mockEventTexts.filter((t) => !doomed.has(t.id));

  return { removed, notFound, failed: [] };
}

// Stornobedingungen (PRD-020 Thema 3) — leer statt vorbelegt: es gibt keinen Altbestand,
// aus dem sich Beispiele ableiten ließen (die Entität entsteht erst mit dieser PRD).
let mockCancellationTerms: CancellationTerm[] = [];

export function getMockCancellationTerms(): CancellationTerm[] {
  return [...mockCancellationTerms].sort((a, b) => a.title.localeCompare(b.title, 'de'));
}

/** Legt eine Stornobedingung an oder ändert sie — wortgleich zu saveMockEventText. */
export function saveMockCancellationTerm(req: SaveCancellationTermRequest): CancellationTerm {
  const jetzt = new Date().toISOString();

  if (!req.id) {
    const angelegt: CancellationTerm = {
      id: `mock-${mockCancellationTerms.length + 1}-${jetzt}`,
      title: req.title.trim(),
      text: req.text,
      createdAt: jetzt,
      modifiedAt: jetzt,
      modifiedBy: 'mock@localhost',
    };
    mockCancellationTerms = [...mockCancellationTerms, angelegt];
    return angelegt;
  }

  const vorhanden = mockCancellationTerms.find((t) => t.id === req.id);
  if (!vorhanden) {
    throw new Error('Diese Stornobedingung existiert nicht mehr.');
  }

  const geaendert: CancellationTerm = {
    ...vorhanden,
    title: req.title.trim(),
    text: req.text,
    modifiedAt: jetzt,
    modifiedBy: 'mock@localhost',
  };

  mockCancellationTerms = mockCancellationTerms.map((t) => (t.id === geaendert.id ? geaendert : t));
  return geaendert;
}

/** Löscht Stornobedingungen — wortgleich zu deleteMockEventTexts. */
export function deleteMockCancellationTerms(ids: string[]): CancellationTermDeleteResult {
  const present = new Set(mockCancellationTerms.map((t) => t.id));

  const removed = ids.filter((id) => present.has(id));
  const notFound = ids.filter((id) => !present.has(id));

  const doomed = new Set(removed);
  mockCancellationTerms = mockCancellationTerms.filter((t) => !doomed.has(t.id));

  return { removed, notFound, failed: [] };
}

// Zahlungstext-Vorlagen (Rich-Text-Umstellung, Teil 3) — vorbelegt mit den Texten aus
// der abgelösten statischen Map (ehemals PAYMENT_NOTE_DEFAULTS), damit der Mock-Modus
// nach der Umstellung nicht schlechter aussieht als davor.
let mockPaymentNoteTemplates: PaymentNoteTemplate[] = [
  {
    id: 'mock-payment-1',
    title: 'Bar/EC oder Überweisung',
    text: 'Bitte begleichen Sie den Betrag bar oder mit EC-Karte. Gerne können Sie den Betrag auch auf unser Konto überweisen. Die Kontoverbindung teilen wir Ihnen bei Buchung mit.',
    paymentMethod: 'depot_or_transfer',
    createdAt: '2026-01-01T00:00:00.000Z',
    modifiedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'mock-payment-2',
    title: 'Bar/EC im Depot',
    text: 'Bitte begleichen Sie den Betrag bar oder mit EC-Karte im Depot.',
    paymentMethod: 'depot',
    createdAt: '2026-01-01T00:00:00.000Z',
    modifiedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'mock-payment-3',
    title: 'Nur Überweisung',
    text: 'Bitte überweisen Sie den Betrag auf unser Konto. Die Kontoverbindung teilen wir Ihnen gerne bei Buchung mit.',
    paymentMethod: 'transfer',
    createdAt: '2026-01-01T00:00:00.000Z',
    modifiedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'mock-payment-4',
    title: 'Rechnung',
    text: 'Der Betrag wird Ihnen nach Anmeldung in Rechnung gestellt.',
    // 'invoice' entfällt als Zahlungsart (22.09.2026-Follow-up) — Text bleibt
    // als frei editierbare Vorlage bestehen, ohne automatische Zuordnung.
    createdAt: '2026-01-01T00:00:00.000Z',
    modifiedAt: '2026-01-01T00:00:00.000Z',
  },
];

export function getMockPaymentNoteTemplates(): PaymentNoteTemplate[] {
  return [...mockPaymentNoteTemplates].sort((a, b) => a.title.localeCompare(b.title, 'de'));
}

/** Legt eine Zahlungstext-Vorlage an oder ändert sie — wortgleich zu saveMockCancellationTerm. */
export function saveMockPaymentNoteTemplate(
  req: SavePaymentNoteTemplateRequest
): PaymentNoteTemplate {
  const jetzt = new Date().toISOString();

  if (!req.id) {
    const angelegt: PaymentNoteTemplate = {
      id: `mock-payment-${mockPaymentNoteTemplates.length + 1}-${jetzt}`,
      title: req.title.trim(),
      text: req.text,
      paymentMethod: req.paymentMethod,
      createdAt: jetzt,
      modifiedAt: jetzt,
      modifiedBy: 'mock@localhost',
    };
    mockPaymentNoteTemplates = [...mockPaymentNoteTemplates, angelegt];
    return angelegt;
  }

  const vorhanden = mockPaymentNoteTemplates.find((t) => t.id === req.id);
  if (!vorhanden) {
    throw new Error('Diese Vorlage existiert nicht mehr.');
  }

  const geaendert: PaymentNoteTemplate = {
    ...vorhanden,
    title: req.title.trim(),
    text: req.text,
    paymentMethod: req.paymentMethod,
    modifiedAt: jetzt,
    modifiedBy: 'mock@localhost',
  };

  mockPaymentNoteTemplates = mockPaymentNoteTemplates.map((t) =>
    t.id === geaendert.id ? geaendert : t
  );
  return geaendert;
}

/** Löscht Zahlungstext-Vorlagen — wortgleich zu deleteMockCancellationTerms. */
export function deleteMockPaymentNoteTemplates(ids: string[]): PaymentNoteTemplateDeleteResult {
  const present = new Set(mockPaymentNoteTemplates.map((t) => t.id));

  const removed = ids.filter((id) => present.has(id));
  const notFound = ids.filter((id) => !present.has(id));

  const doomed = new Set(removed);
  mockPaymentNoteTemplates = mockPaymentNoteTemplates.filter((t) => !doomed.has(t.id));

  return { removed, notFound, failed: [] };
}

let mockFinancialSupport: FinancialSupport[] = [
  {
    id: 'mock-fs-1',
    name: 'Weinseminar-Pauschale',
    audience: EventAudience.Closed,
    flatRateAmount: 100,
    vaTypes: ['vt-seminar'],
    proofRequired: true,
    createdAt: '2026-01-01T00:00:00.000Z',
    createdBy: 'mock@localhost',
    modifiedAt: '2026-01-01T00:00:00.000Z',
    modifiedBy: 'mock@localhost',
  },
];

export function getMockFinancialSupport(): FinancialSupport[] {
  return [...mockFinancialSupport].sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

/** Legt einen Eintrag der finanziellen Unterstützung an oder ändert ihn. */
export function saveMockFinancialSupport(req: SaveFinancialSupportRequest): FinancialSupport {
  const jetzt = new Date().toISOString();

  const konflikt = mockFinancialSupport.find(
    (e) => e.id !== req.id && e.audience === req.audience && e.vaTypes.some((v) => req.vaTypes.includes(v))
  );
  if (konflikt) {
    const ueberlappend = konflikt.vaTypes.filter((v) => req.vaTypes.includes(v));
    const namen = ueberlappend
      .map((id) => EVENT_TYPES.find((et) => et.id === id)?.name ?? id)
      .join(', ');
    const bezeichnung = ueberlappend.length === 1 ? 'Veranstaltungstyp' : 'Veranstaltungstypen';
    throw new Error(`${bezeichnung} "${namen}" bereits in "${konflikt.name}" zugewiesen.`);
  }

  if (!req.id) {
    const angelegt: FinancialSupport = {
      id: `mock-fs-${mockFinancialSupport.length + 1}-${jetzt}`,
      name: req.name.trim(),
      audience: req.audience,
      flatRateAmount: req.flatRateAmount,
      vaTypes: req.vaTypes,
      proofRequired: req.proofRequired,
      createdAt: jetzt,
      createdBy: 'mock@localhost',
      modifiedAt: jetzt,
      modifiedBy: 'mock@localhost',
    };
    mockFinancialSupport = [...mockFinancialSupport, angelegt];
    return angelegt;
  }

  const vorhanden = mockFinancialSupport.find((e) => e.id === req.id);
  if (!vorhanden) {
    throw new Error('Dieser Eintrag existiert nicht mehr.');
  }

  const geaendert: FinancialSupport = {
    ...vorhanden,
    name: req.name.trim(),
    audience: req.audience,
    flatRateAmount: req.flatRateAmount,
    vaTypes: req.vaTypes,
    proofRequired: req.proofRequired,
    modifiedAt: jetzt,
    modifiedBy: 'mock@localhost',
  };

  mockFinancialSupport = mockFinancialSupport.map((e) => (e.id === geaendert.id ? geaendert : e));
  return geaendert;
}

/** Löscht Einträge der finanziellen Unterstützung — wortgleich zu deleteMockPaymentNoteTemplates. */
export function deleteMockFinancialSupport(ids: string[]): FinancialSupportDeleteResult {
  const present = new Set(mockFinancialSupport.map((e) => e.id));

  const removed = ids.filter((id) => present.has(id));
  const notFound = ids.filter((id) => !present.has(id));

  const doomed = new Set(removed);
  mockFinancialSupport = mockFinancialSupport.filter((e) => !doomed.has(e.id));

  return { removed, notFound, failed: [] };
}

// Anmeldetext-Vorlagen — wortgleich zu den Zahlungstext-Vorlagen, vorbelegt mit den
// Texten aus der abgelösten statischen Map (ehemals REGISTRATION_NOTE_DEFAULTS).
let mockRegistrationNoteTemplates: RegistrationNoteTemplate[] = [
  {
    id: 'mock-registration-1',
    title: 'Telefonisch oder Kontaktformular',
    text: 'Ihre verbindliche Anmeldung nehmen wir gerne telefonisch oder über unser Kontaktformular entgegen.',
    createdAt: '2026-01-01T00:00:00.000Z',
    modifiedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'mock-registration-2',
    title: 'Kontaktformular',
    text: 'Ihre verbindliche Anmeldung nehmen wir gerne über unser Kontaktformular entgegen.',
    createdAt: '2026-01-01T00:00:00.000Z',
    modifiedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'mock-registration-3',
    title: 'Im Depot',
    text: 'Auf Ihre verbindliche Anmeldung freuen wir uns bei Ihrem persönlichen Besuch in unserem Depot.',
    createdAt: '2026-01-01T00:00:00.000Z',
    modifiedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'mock-registration-4',
    title: 'Telefonisch',
    text: 'Ihre verbindliche Anmeldung nehmen wir gerne telefonisch entgegen.',
    createdAt: '2026-01-01T00:00:00.000Z',
    modifiedAt: '2026-01-01T00:00:00.000Z',
  },
  {
    id: 'mock-registration-5',
    title: 'Online',
    text: 'Ihre verbindliche Anmeldung nehmen wir gerne über unser Online-Formular entgegen.',
    createdAt: '2026-01-01T00:00:00.000Z',
    modifiedAt: '2026-01-01T00:00:00.000Z',
  },
];

export function getMockRegistrationNoteTemplates(): RegistrationNoteTemplate[] {
  return [...mockRegistrationNoteTemplates].sort((a, b) => a.title.localeCompare(b.title, 'de'));
}

/** Legt eine Anmeldetext-Vorlage an oder ändert sie — wortgleich zu saveMockPaymentNoteTemplate. */
export function saveMockRegistrationNoteTemplate(
  req: SaveRegistrationNoteTemplateRequest
): RegistrationNoteTemplate {
  const jetzt = new Date().toISOString();

  if (!req.id) {
    const angelegt: RegistrationNoteTemplate = {
      id: `mock-registration-${mockRegistrationNoteTemplates.length + 1}-${jetzt}`,
      title: req.title.trim(),
      text: req.text,
      createdAt: jetzt,
      modifiedAt: jetzt,
      modifiedBy: 'mock@localhost',
    };
    mockRegistrationNoteTemplates = [...mockRegistrationNoteTemplates, angelegt];
    return angelegt;
  }

  const vorhanden = mockRegistrationNoteTemplates.find((t) => t.id === req.id);
  if (!vorhanden) {
    throw new Error('Diese Vorlage existiert nicht mehr.');
  }

  const geaendert: RegistrationNoteTemplate = {
    ...vorhanden,
    title: req.title.trim(),
    text: req.text,
    modifiedAt: jetzt,
    modifiedBy: 'mock@localhost',
  };

  mockRegistrationNoteTemplates = mockRegistrationNoteTemplates.map((t) =>
    t.id === geaendert.id ? geaendert : t
  );
  return geaendert;
}

/** Löscht Anmeldetext-Vorlagen — wortgleich zu deleteMockPaymentNoteTemplates. */
export function deleteMockRegistrationNoteTemplates(
  ids: string[]
): RegistrationNoteTemplateDeleteResult {
  const present = new Set(mockRegistrationNoteTemplates.map((t) => t.id));

  const removed = ids.filter((id) => present.has(id));
  const notFound = ids.filter((id) => !present.has(id));

  const doomed = new Set(removed);
  mockRegistrationNoteTemplates = mockRegistrationNoteTemplates.filter((t) => !doomed.has(t.id));

  return { removed, notFound, failed: [] };
}

export function getMockDepots(): DepotRef[] {
  return [...DEPOTS];
}

// ---- Mutators (lifecycle-aware) ---------------------------------------------

const nowIso = () => new Date().toISOString();

export function createMockEvent(payload: CreateEventRequest, actor = 'mock-user'): DepotEvent {
  const id = nextId();
  const created: DepotEvent = {
    ...payload,
    identifier: id,
    ctId: id,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    createdBy: actor,
    modifiedBy: actor,
    status: EventStatus.Initial,
  };
  mockEvents = [created, ...mockEvents];
  return { ...created };
}

/**
 * Mock counterpart of POST /api/events/:id/series (24.09.2026) — clones
 * `source` once per occurrence (everything but startAt/endAt/
 * registrationDeadline/paymentDeadline/plannedOnlineDate, and a fresh slug),
 * validated the same way as the real backend's create-event-series.ts.
 * Simpler than the real one on purpose (a single thrown Error on the first
 * invalid occurrence, not per-field/per-occurrence detail) — same fidelity
 * level as every other mock mutator in this file; mock mode is for local
 * dev, not for exercising the exact error contract.
 */
export function createMockEventSeries(
  source: DepotEvent,
  occurrences: EventSeriesOccurrence[],
  createAs: 'initial' | 'in_review',
  actor = 'mock-user'
): CreateEventSeriesResult {
  if (source.status === EventStatus.Rejected) {
    throw new Error('Aus einer nicht freigegebenen Veranstaltung kann keine Serie erstellt werden.');
  }
  if (occurrences.length === 0) {
    throw new Error('Mindestens ein Zeitraum wird benötigt.');
  }

  occurrences.forEach((occ, index) => {
    if (!occ.startAt || !occ.endAt || !occ.plannedOnlineDate) {
      throw new Error(`Zeitraum ${index + 1}: Datum/Uhrzeit und Onlinestellung sind Pflichtfelder.`);
    }
    const onlineDay = occ.plannedOnlineDate.slice(0, 10);
    if (source.registrationRequired) {
      if (!occ.registrationDeadline) {
        throw new Error(`Zeitraum ${index + 1}: Anmeldefrist fehlt.`);
      }
      if (occ.registrationDeadline.slice(0, 10) < onlineDay) {
        throw new Error(`Zeitraum ${index + 1}: Anmeldefrist darf nicht vor der Onlinestellung liegen.`);
      }
    }
    if (source.costEnabled) {
      if (!occ.paymentDeadline) {
        throw new Error(`Zeitraum ${index + 1}: Zahlungsfrist fehlt.`);
      }
      if (occ.paymentDeadline.slice(0, 10) < onlineDay) {
        throw new Error(`Zeitraum ${index + 1}: Zahlungsfrist darf nicht vor der Onlinestellung liegen.`);
      }
    }
  });

  const {
    identifier: _identifier,
    ctId: _ctId,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    createdBy: _createdBy,
    modifiedBy: _modifiedBy,
    createdByRole: _createdByRole,
    status: _status,
    available: _available,
    sold: _sold,
    ticketed: _ticketed,
    refundStatus: _refundStatus,
    rejectionReason: _rejectionReason,
    availableMailings: _availableMailings,
    eventsVersion: _eventsVersion,
    slug: _slug,
    ...baseInput
  } = source;

  const created = occurrences.map((occ) =>
    createMockEvent(
      {
        ...(baseInput as CreateEventRequest),
        startAt: occ.startAt,
        endAt: occ.endAt,
        // Pro Termin wählbar (25.09.2026, Nutzervorgabe) — spiegelt den
        // echten Handler (create-event-series.ts).
        businessHours: occ.businessHours,
        plannedOnlineDate: occ.plannedOnlineDate,
        registrationDeadline: occ.registrationDeadline,
        paymentDeadline: occ.paymentDeadline,
      },
      actor
    )
  );

  const finalized =
    createAs === 'in_review' ? created.map((event) => submitMockEvent(event.identifier, actor)) : created;

  return { created: finalized, failed: [] };
}

function findIndexOrThrow(identifier: string): number {
  const idx = mockEvents.findIndex((e) => e.identifier === identifier);
  if (idx === -1) throw new Error(`Event not found: ${identifier}`);
  return idx;
}

export function updateMockEvent(payload: UpdateEventRequest, actor = 'mock-user'): DepotEvent {
  const idx = findIndexOrThrow(payload.identifier);
  const current = mockEvents[idx];
  const updated: DepotEvent = {
    ...current,
    ...payload,
    updatedAt: nowIso(),
    modifiedBy: actor,
    // Editing an in_review event resets it to initial (review integrity).
    status: current.status === EventStatus.InReview ? EventStatus.Initial : current.status,
  };
  mockEvents[idx] = updated;
  return { ...updated };
}

/** Removes an event. Throws when it does not exist, like the other mutators. */
export function deleteMockEvent(identifier: string): void {
  mockEvents.splice(findIndexOrThrow(identifier), 1);
}

function transition(
  identifier: string,
  allowedFrom: EventStatus[],
  toStatus: EventStatus,
  actor: string,
  extra?: Partial<DepotEvent>
): DepotEvent {
  const idx = findIndexOrThrow(identifier);
  const current = mockEvents[idx];
  if (!allowedFrom.includes(current.status)) {
    throw new Error(
      `Invalid transition ${current.status} -> ${toStatus} for ${identifier}`
    );
  }
  const updated: DepotEvent = {
    ...current,
    ...extra,
    status: toStatus,
    updatedAt: nowIso(),
    modifiedBy: actor,
  };
  mockEvents[idx] = updated;
  return { ...updated };
}

export function submitMockEvent(identifier: string, actor = 'mock-user'): DepotEvent {
  return transition(identifier, [EventStatus.Initial, EventStatus.Rejected], EventStatus.InReview, actor, {
    rejectionReason: undefined,
  });
}

/**
 * Lands directly on Online (24.09.2026) when the planned online date has
 * already arrived — mirrors createApproveEventHandler's server-side
 * shortcut, so mock mode's status list matches the real backend for this
 * common case (approving after the requested Monday has already passed).
 */
export function approveMockEvent(identifier: string, actor = 'mock-user'): DepotEvent {
  const current = getMockEvent(identifier);
  const today = nowIso().slice(0, 10);
  const onlineDay = (current.plannedOnlineDate ?? '').slice(0, 10);
  const toStatus =
    onlineDay && onlineDay <= today ? EventStatus.Online : EventStatus.Approved;
  return transition(identifier, [EventStatus.InReview], toStatus, actor);
}

export function rejectMockEvent(
  identifier: string,
  reason: string,
  actor = 'mock-user'
): DepotEvent {
  return transition(identifier, [EventStatus.InReview], EventStatus.Rejected, actor, {
    rejectionReason: reason,
  });
}

export function reopenMockEvent(
  identifier: string,
  toStatus: EventStatus.InReview | EventStatus.Initial = EventStatus.InReview,
  actor = 'mock-user'
): DepotEvent {
  return transition(identifier, [EventStatus.Approved, EventStatus.Online], toStatus, actor);
}

/** Mirrors the backend's ALLOWED_STATUS_TRANSITIONS/PARTNER_ALLOWED_TRANSITIONS for
 * cancel — the date/role gate lives server-side (access-control.ts), so mock mode
 * does not re-check it here, same as the other transitions above. */
export function cancelMockEvent(identifier: string, actor = 'mock-user'): DepotEvent {
  return transition(
    identifier,
    [
      EventStatus.Initial,
      EventStatus.Rejected,
      EventStatus.InReview,
      EventStatus.Approved,
      EventStatus.Online,
    ],
    EventStatus.Cancelled,
    actor
  );
}

export function completeMockEvent(identifier: string, actor = 'mock-user'): DepotEvent {
  return transition(identifier, [EventStatus.Approved, EventStatus.Online], EventStatus.Completed, actor);
}

/**
 * Mock for the notify-advertising-depots mutation. There is no communication-
 * engine to call in mock mode, so every advertising depot is reported "sent" —
 * good enough to exercise the confirmation dialog and toast without a backend.
 */
export function notifyAdvertisingDepotsMockEvent(identifier: string): NotifyAdvertisingDepotsResult {
  const event = getMockEvent(identifier);
  const organizing = event.depots[0]?.key ?? '?';
  return {
    sent: event.promotingDepots.map((d) => `${organizing}->${d.key}`),
    failed: [],
  };
}
